import { cn } from '@ooh/ui/lib/utils';
import type { Client } from '@ooh/contracts';
import type {
  QuickAddCreateDraftFormValues,
  QuickAddOfferPickerMode,
  QuickAddOfferPickerProps,
} from './quick-add-offer-picker';
import {
  QuickAddOfferPickerBody,
  QuickAddOfferPickerFooter,
  QuickAddOfferPickerHeader,
} from './quick-add-offer-picker-sections';

export interface QuickAddOfferPickerPanelProps extends Omit<
  QuickAddOfferPickerProps,
  'onOpenFullForm'
> {
  mode: QuickAddOfferPickerMode;
  onModeChange: (mode: QuickAddOfferPickerMode) => void;
  selectedDraftId: string | null;
  /**
   * Narrowed back to a plain trigger (no args) for this internal
   * Panel/Body/Section/Button chain — `QuickAddOfferPicker` is the only layer
   * that knows the picker's live form values and builds the richer
   * `QuickAddOfferPickerOpenFullFormArgs` payload for the caller (CU-86cb8grgn).
   */
  onOpenFullForm?: () => void;
  createDraftForm: {
    control: import('react-hook-form').Control<QuickAddCreateDraftFormValues>;
    formState: import('react-hook-form').FormState<QuickAddCreateDraftFormValues>;
    register: import('react-hook-form').UseFormRegister<QuickAddCreateDraftFormValues>;
    clearErrors: import('react-hook-form').UseFormClearErrors<QuickAddCreateDraftFormValues>;
  };
  formId?: string;
  createDraftConfirmDisabled?: boolean;
  /** True when the caller is sales-department but lacks offer.assign_other_salesperson. */
  salespersonFieldDisabled?: boolean;
  salesUserOptions: Array<{ label: string; value: string }>;
  /** The selected client's brands, or an empty array when no client is selected. */
  selectedClientBrands?: string[];
  onClientCreated?: (client: Client, wasCreated: boolean) => void;
  addCoCreators?: boolean;
  onAddCoCreatorsChange?: (checked: boolean) => void;
  onCreateDraftSubmit?: () => void;
  onExistingConfirm?: () => void;
  showFooter?: boolean;
  showHeader?: boolean;
}

function getPanelTestId(presentation: QuickAddOfferPickerPanelProps['presentation']) {
  return presentation === 'modal'
    ? 'quick-add-offer-picker-panel'
    : 'quick-add-offer-picker-popover';
}

function shouldUseOwnChrome({
  presentation,
  showHeader,
  showFooter,
}: Pick<QuickAddOfferPickerPanelProps, 'presentation' | 'showHeader' | 'showFooter'>) {
  if (presentation === 'popover') {
    return true;
  }

  return Boolean(showHeader && showFooter);
}

export function QuickAddOfferPickerPanel(props: QuickAddOfferPickerPanelProps) {
  const { presentation, showFooter = true, showHeader = true, title, subtitle } = props;
  const testId = getPanelTestId(presentation);
  const hasOwnChrome = shouldUseOwnChrome({ presentation, showHeader, showFooter });

  // `QuickAddOfferPickerHeader`/`Body`/`Footer` render with no padding of their own by
  // design — for `presentation="modal"` their host (`ModalDialog`) supplies `px-6`
  // header/body/footer padding around them. `hasOwnChrome` panels (popover) have no
  // such host, so this panel must supply equivalent padding itself, or the header
  // title, offer-picker card, and action buttons render flush against this panel's
  // own border with no breathing room.
  const sectionPaddingClass = hasOwnChrome ? 'px-4' : undefined;

  return (
    <div
      className={cn(
        // Fill the host container's width instead of hardcoding a fixed pixel width
        // for `presentation="popover"` — the popover host (e.g. `UnifiedSelectPopover`)
        // owns the outer width. A previous fixed `w-[420px]` here, embedded inside an
        // outer popover sized independently at 288px, caused this panel's own bordered
        // chrome to overflow the host and visually collide with sibling content
        // (header/action buttons).
        'flex w-full flex-col bg-background',
        hasOwnChrome && 'overflow-hidden rounded-xl border border-border/60 shadow-sm',
      )}
      data-testid={testId}
    >
      {showHeader ? (
        <div
          className={cn(sectionPaddingClass, hasOwnChrome && 'border-b border-border/50 pt-4 pb-3')}
        >
          <QuickAddOfferPickerHeader title={title} subtitle={subtitle} />
        </div>
      ) : null}
      <div className={cn(sectionPaddingClass, hasOwnChrome && 'py-3')}>
        <QuickAddOfferPickerBody {...props} />
      </div>
      {showFooter ? (
        <div
          className={cn(sectionPaddingClass, hasOwnChrome && 'border-t border-border/50 pt-3 pb-4')}
        >
          <QuickAddOfferPickerFooter {...props} />
        </div>
      ) : null}
    </div>
  );
}
